export class WebCryptoUtils {
    private static key: CryptoKey | null = null;
    
    private static async getKey(): Promise<CryptoKey> {
        if (!this.key) {
            // For now, we'll use a static key derived from a password
            // TODO: This should be improved to use session-based or server-provided keys
            const keyMaterial = await crypto.subtle.importKey(
                'raw',
                new TextEncoder().encode('appmed-patient-data-encryption-2024'),
                { name: 'PBKDF2' },
                false,
                ['deriveKey']
            );
            
            this.key = await crypto.subtle.deriveKey(
                {
                    name: 'PBKDF2',
                    salt: new TextEncoder().encode('appmed-salt-2024'),
                    iterations: 100000,
                    hash: 'SHA-256'
                },
                keyMaterial,
                { name: 'AES-GCM', length: 256 },
                false,
                ['encrypt', 'decrypt']
            );
        }
        return this.key;
    }

    static async encrypt(data: string): Promise<string> {
        try {
            const key = await this.getKey();
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encoded = new TextEncoder().encode(data);
            
            const encrypted = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv },
                key,
                encoded
            );
            
            // Combine IV and encrypted data
            const combined = new Uint8Array(iv.length + encrypted.byteLength);
            combined.set(iv);
            combined.set(new Uint8Array(encrypted), iv.length);
            
            // Convert to base64 for storage
            return btoa(String.fromCharCode(...combined));
        } catch (error) {
            console.error('Encryption failed:', error);
            throw new Error('Failed to encrypt data');
        }
    }

    static async decrypt(encryptedData: string): Promise<string> {
        try {
            const key = await this.getKey();
            
            // Convert from base64 and extract IV and encrypted data
            const combined = new Uint8Array([...atob(encryptedData)].map(char => char.charCodeAt(0)));
            
            const iv = combined.slice(0, 12);
            const encrypted = combined.slice(12);
            
            const decrypted = await crypto.subtle.decrypt(
                { name: 'AES-GCM', iv },
                key,
                encrypted
            );
            
            return new TextDecoder().decode(decrypted);
        } catch (error) {
            console.error('Decryption failed:', error);
            throw new Error('Failed to decrypt data');
        }
    }

    /**
     * Clear the cached encryption key (useful for logout or key rotation)
     */
    static clearKey(): void {
        this.key = null;
    }
} 